/**
 * PaymentRotationDialog — iter228
 *
 * Group-wide "who should pay for the venue" scheduler. Two modes:
 *   - Monthly     — pick a payer per calendar month
 *   - Round-robin — auto-advance through an ordered list each recorded payment
 *
 * The SessionPaymentDialog uses this data via GET /sessions/{id}/suggested-payer
 * to pre-fill the payer dropdown.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Plus, X, ArrowRight, Calendar, RefreshCw, RotateCw, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const MODES = [
  { id: 'off', label: 'Off', help: 'No pre-fill. Admin picks each time.' },
  { id: 'monthly', label: 'Monthly', help: 'One payer for the whole month.' },
  { id: 'round_robin', label: 'Round-robin', help: 'Auto-advance each recorded payment.' },
];

function nextMonthKey(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function PaymentRotationDialog({ open, groupId, group, isAdmin, onClose }) {
  const [rotation, setRotation] = useState({ mode: 'off', payers: [], monthly_schedule: {}, round_robin_cursor_index: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoFillMonth, setAutoFillMonth] = useState(nextMonthKey(0));
  const [autoFillCount, setAutoFillCount] = useState(12);

  const memberOptions = useMemo(() => (group?.members || []).map((m) => ({ id: m.id, name: m.name })), [group]);
  const nameById = useMemo(() => {
    const m = {};
    memberOptions.forEach((x) => { m[x.id] = x.name; });
    return m;
  }, [memberOptions]);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const r = await api.get(`/groups/${groupId}/payment-rotation`);
      setRotation({
        mode: r.data?.mode || 'off',
        payers: r.data?.payers || [],
        monthly_schedule: r.data?.monthly_schedule || {},
        round_robin_cursor_index: r.data?.round_robin_cursor_index || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load rotation');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/groups/${groupId}/payment-rotation`, rotation);
      toast.success('Rotation saved');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addPayer = (uid) => {
    if (!uid || rotation.payers.includes(uid)) return;
    setRotation((r) => ({ ...r, payers: [...r.payers, uid] }));
  };
  const removePayer = (uid) => setRotation((r) => ({ ...r, payers: r.payers.filter((x) => x !== uid) }));
  const movePayer = (idx, dir) => {
    setRotation((r) => {
      const next = [...r.payers];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return r;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...r, payers: next };
    });
  };

  const setMonth = (key, uid) => {
    setRotation((r) => ({ ...r, monthly_schedule: { ...r.monthly_schedule, [key]: uid || '' } }));
  };
  const clearMonth = (key) => {
    setRotation((r) => {
      const s = { ...r.monthly_schedule };
      delete s[key];
      return { ...r, monthly_schedule: s };
    });
  };

  const autoFill = async () => {
    if (rotation.payers.length === 0) {
      toast.error('Add at least one payer to the rotation first');
      return;
    }
    // Save first (so backend has fresh payers list), then auto-fill
    setSaving(true);
    try {
      await api.put(`/groups/${groupId}/payment-rotation`, rotation);
      const r = await api.post(`/groups/${groupId}/payment-rotation/auto-fill`, {
        start_month: autoFillMonth,
        months: Number(autoFillCount || 12),
      });
      toast.success(r.data?.message || 'Filled');
      setRotation((prev) => ({
        ...prev,
        monthly_schedule: r.data?.rotation?.monthly_schedule || prev.monthly_schedule,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-fill failed');
    } finally {
      setSaving(false);
    }
  };

  const monthKeys = Object.keys(rotation.monthly_schedule || {}).sort();

  // iter229 — Swap two months (each row gets a ⇄ button that opens a mini picker)
  const [swapFrom, setSwapFrom] = useState(null); // month key that initiated the swap
  const startSwap = (monthKey) => setSwapFrom(monthKey === swapFrom ? null : monthKey);
  const doSwap = async (monthB) => {
    if (!swapFrom || swapFrom === monthB) { setSwapFrom(null); return; }
    setSaving(true);
    try {
      // Save any local edits first so backend has fresh state
      await api.put(`/groups/${groupId}/payment-rotation`, rotation);
      const r = await api.post(`/groups/${groupId}/payment-rotation/swap`, {
        month_a: swapFrom, month_b: monthB,
      });
      toast.success(r.data?.message || 'Swapped');
      setRotation((prev) => ({
        ...prev,
        monthly_schedule: r.data?.rotation?.monthly_schedule || prev.monthly_schedule,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Swap failed');
    } finally {
      setSaving(false);
      setSwapFrom(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="rotation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCw className="w-4 h-4" /> Payment Rotation
          </DialogTitle>
          <DialogDescription>
            Pre-fill the &quot;Who paid?&quot; modal so admins can one-tap save. Applies group-wide.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Mode picker */}
            <div>
              <Label className="text-xs">Mode</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setRotation((r) => ({ ...r, mode: m.id }))}
                    className={`p-2 border-2 rounded-lg text-xs font-semibold transition-colors ${
                      rotation.mode === m.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                    }`}
                    data-testid={`rotation-mode-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {MODES.find((m) => m.id === rotation.mode)?.help}
              </p>
            </div>

            {rotation.mode !== 'off' && (
              <>
                {/* Payers list */}
                <div>
                  <Label className="text-xs">Payers {rotation.mode === 'round_robin' && <span className="text-muted-foreground">(order matters)</span>}</Label>
                  <div className="mt-1 space-y-1">
                    {rotation.payers.map((uid, idx) => (
                      <div key={uid} className="flex items-center gap-2 bg-muted/30 border rounded-lg px-2 py-1.5" data-testid={`rotation-payer-${uid}`}>
                        <span className="text-xs w-5 h-5 bg-primary/20 text-primary rounded-full flex items-center justify-center font-bold shrink-0">
                          {idx + 1}
                        </span>
                        <span className="flex-1 text-sm truncate">{nameById[uid] || uid}</span>
                        {rotation.mode === 'round_robin' && idx === (rotation.round_robin_cursor_index || 0) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 font-semibold">
                            NEXT
                          </span>
                        )}
                        {rotation.mode === 'round_robin' && (
                          <>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => movePayer(idx, -1)} disabled={idx === 0} title="Move up">
                              ↑
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => movePayer(idx, 1)} disabled={idx === rotation.payers.length - 1} title="Move down">
                              ↓
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removePayer(uid)} data-testid={`rotation-remove-payer-${uid}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Select value="" onValueChange={addPayer}>
                      <SelectTrigger data-testid="rotation-add-payer-select">
                        <SelectValue placeholder="+ Add payer" />
                      </SelectTrigger>
                      <SelectContent>
                        {memberOptions.filter((m) => !rotation.payers.includes(m.id)).map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Monthly schedule (only when mode='monthly') */}
                {rotation.mode === 'monthly' && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Monthly schedule</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="month"
                          value={autoFillMonth}
                          onChange={(e) => setAutoFillMonth(e.target.value)}
                          className="h-7 text-xs w-32"
                          data-testid="rotation-autofill-start"
                        />
                        <Input
                          type="number"
                          min={1}
                          max={36}
                          value={autoFillCount}
                          onChange={(e) => setAutoFillCount(e.target.value)}
                          className="h-7 text-xs w-14"
                          data-testid="rotation-autofill-count"
                        />
                        <Button size="sm" variant="outline" onClick={autoFill} className="h-7 text-xs" data-testid="rotation-autofill-btn">
                          <RefreshCw className="w-3 h-3 mr-1" /> Fill
                        </Button>
                      </div>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      {monthKeys.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-3">
                          No months scheduled. Click <span className="font-semibold">Fill</span> to seed from payers, or add manually below.
                        </p>
                      ) : (
                        <div className="divide-y">
                          {monthKeys.map((key) => (
                            <div key={key} className={`flex items-center gap-2 px-2 py-1.5 ${swapFrom === key ? 'bg-primary/10' : ''}`} data-testid={`rotation-month-${key}`}>
                              <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="text-xs w-20 font-mono">{key}</span>
                              <ArrowRight className="w-3 h-3 text-muted-foreground" />
                              {swapFrom && swapFrom !== key ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => doSwap(key)}
                                  className="h-7 text-xs flex-1"
                                  data-testid={`rotation-swap-target-${key}`}
                                >
                                  Swap with {swapFrom} →
                                </Button>
                              ) : (
                                <Select
                                  value={rotation.monthly_schedule[key] || ''}
                                  onValueChange={(v) => setMonth(key, v)}
                                >
                                  <SelectTrigger className="h-7 text-xs flex-1" data-testid={`rotation-month-select-${key}`}>
                                    <SelectValue placeholder="Pick payer" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {memberOptions.map((m) => (
                                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              <Button
                                size="sm"
                                variant={swapFrom === key ? 'default' : 'ghost'}
                                className="h-6 w-6 p-0 shrink-0"
                                onClick={() => startSwap(key)}
                                title={swapFrom === key ? 'Cancel swap' : 'Swap with another month'}
                                data-testid={`rotation-swap-btn-${key}`}
                              >
                                <ArrowLeftRight className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => clearMonth(key)} title="Remove month">
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        type="month"
                        placeholder="YYYY-MM"
                        onChange={(e) => e.target.value && setMonth(e.target.value, rotation.payers[0] || '')}
                        className="h-7 text-xs flex-1"
                        data-testid="rotation-add-month"
                      />
                      <span className="text-xs text-muted-foreground">Add a month manually</span>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="rotation-cancel">
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={saving || !isAdmin} data-testid="rotation-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
